import { GoogleMap, InfoWindow, Marker, useLoadScript } from '@react-google-maps/api';
import type { FC } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import type { IFindDepartmentModel, IMapProps } from 'types';
import { useDetectClickOutside } from '../../foundation/methods/customHooks/detectClickOutside';
import { LoadingSpinner } from '../fa-spinner/fa-spinner';
import { blueGoogleMapsMarker, redGoogleMapsMarker } from './fa-google-maps.markers';

export const GoogleMapComponent: FC<IMapProps> = ({ offices, googleMapsApiKey, isLocationPage }: IMapProps) => {
    const { isLoaded } = useLoadScript({ googleMapsApiKey: googleMapsApiKey ?? '' });

    if (!isLoaded) {
        return (
            <article className="fa-spinner__container">
                <LoadingSpinner />
            </article>
        );
    }

    return <Map offices={offices} isLocationPage={isLocationPage} />;
};

const Map: FC<IMapProps> = ({ offices, isLocationPage }: IMapProps) => {
    const [activeMarker, setActiveMarker] = useState<string | null>(null);
    const [localMapInstance, setLocalMapInstance] = useState<any>();
    const infoWindowRef = useRef<HTMLHeadingElement>(null);
    const markerList = offices ?? [];
    const el = '.fa-component--department-finder';
    const DOM = document.querySelector(el);
    const collaboratorOfficeLabel = DOM ? DOM.getAttribute('collaborator-office-label') ?? '' : '';
    const falckOfficeLabel = DOM ? DOM.getAttribute('falck-office-label') ?? '' : '';
    const bounds = new google.maps.LatLngBounds();
    const mapCenter = bounds.getCenter();
    const mapOptions = { controlSize: 25, maxZoom: 15 };

    useDetectClickOutside(infoWindowRef, () => {
        setActiveMarker(null);
    });

    useEffect(() => {
        if (localMapInstance) {
            fitAndZoomToMarkers(localMapInstance);
        }
    }, [offices]);

    const fitAndZoomToMarkers = (mapInstance: any) => {
        mapInstance.setCenter(mapCenter);
        mapInstance.panToBounds(bounds);
        mapInstance.fitBounds(bounds);
    };

    const renderMap = () => {
        const visableMarkers = markerList.filter((i) => i.IsVisible);

        visableMarkers.forEach((item) => bounds.extend(item.GeoLocation)); //extends the bounds area with the specificed pins.

        const onLoad = React.useCallback(
            // wrapping to a function is used to access the mapInstance.
            function onLoad(mapInstance: any) {
                setLocalMapInstance(mapInstance);
                fitAndZoomToMarkers(mapInstance);
            },
            [],
        );

        return (
            <GoogleMap
                onLoad={onLoad}
                options={mapOptions}
                mapContainerClassName="fa-google-maps"
                onClick={() => {
                    setActiveMarker(null);
                }}
            >
                {visableMarkers.map((marker, index) => (
                    <Marker
                        visible={marker.IsVisible}
                        icon={computeMarkerColor(marker)}
                        position={marker.GeoLocation}
                        key={index}
                        onClick={() => {
                            setActiveMarker(marker.Id);
                        }}
                    >
                        {renderInfoWindow(marker)}
                    </Marker>
                ))}
            </GoogleMap>
        );
    };

    const computeMarkerColor = (marker: IFindDepartmentModel.IOffice) => {
        const svgMarker = {
            url: redGoogleMapsMarker,
            anchor: new google.maps.Point(15, 30),
        };

        svgMarker.url = marker?.IsPartner ? blueGoogleMapsMarker : redGoogleMapsMarker;

        return svgMarker;
    };

    const renderInfoWindow = (marker: IFindDepartmentModel.IOffice) =>
        activeMarker === marker.Id ? (
            <InfoWindow
                onCloseClick={() => {
                    setActiveMarker(null);
                }}
            >
                <article className="fa-google-maps__info-window" ref={infoWindowRef}>
                    <h2 className="fa-google-maps__info-window-title fa-theme-color">{marker?.OfficeName}</h2>
                    <p className="fa-rich-text fa-theme-color">
                        {marker?.IsPartner ? collaboratorOfficeLabel : falckOfficeLabel}
                    </p>
                    {!isLocationPage && marker?.Url && (
                        <a href={marker?.Url} className="fa-atom-button">
                            {marker?.ViewDetailsLabel}
                        </a>
                    )}
                    {isLocationPage && marker?.GoogleMapsShareLink && (
                        <a
                            href={marker?.GoogleMapsShareLink}
                            target="_blank"
                            className="fa-atom-button"
                            rel="noreferrer"
                        >
                            {marker?.GetDirectionsLabel}
                        </a>
                    )}
                </article>
            </InfoWindow>
        ) : null;

    return <React.Fragment>{renderMap()}</React.Fragment>;
};
